import { designTokens } from "@itandi/itandi-bb-ui";
import { Alertv2 } from "@itandi/itandi-bb-ui/Alertv2";
import { Paragraph } from "@itandi/itandi-bb-ui/Paragraph";
import { useState } from "react";
import { toast } from "react-toastify";
import { ConfirmModal } from "~/components/common/parts/modal/ConfirmModal";
import { StatusBar } from "~/components/common/parts/status/StatusBar";
import { useDealContext, useMutateDealContext } from "~/context/DealContext";
import { getOriginVendor } from "~/models/DealVendorCompany";
import type { Status } from "~/models/Status";
import {
	getWithVendorStatuses,
	isCanceled,
	isCompleted
} from "~/models/Status";
import { isSuccess } from "~/utils/api/api_handler";
import { create as postCancel } from "~/utils/api/vendors/deals/DealCancel";
import { create as postCancelBack } from "~/utils/api/vendors/deals/DealCancelBack";
import Styles from "./DealMainWorkflow.module.scss";

type Props = {
	isSubVendor: boolean;
	statuses: Status[];
};

export function DealMainWorkflowVendorCancel({ isSubVendor, statuses }: Props) {
	const deal = useDealContext();
	const mutateDeal = useMutateDealContext();
	const displayStatuses = getWithVendorStatuses(statuses);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [confirmBackOpen, setConfirmBackOpen] = useState(false);

	const handleConfirmOpen = () => {
		setConfirmOpen(true);
	};
	const handleConfirmClose = () => {
		setConfirmOpen(false);
	};
	const handleConfirmBackOpen = () => {
		setConfirmBackOpen(true);
	};
	const handleConfirmBackClose = () => {
		setConfirmBackOpen(false);
	};

	const handleAcceptCancel = async (): Promise<void> => {
		setIsSubmitting(true);
		const result = await postCancel(deal.id);
		if (isSuccess(result)) {
			await mutateDeal();
			toast.success("取り下げを受理しました。");
			handleConfirmClose();
		} else {
			toast.error("エラーが発生しました。");
		}
		setIsSubmitting(false);
	};

	const handleBackCancel = async (): Promise<void> => {
		setIsSubmitting(true);
		const result = await postCancelBack(deal.id);
		if (isSuccess(result)) {
			await mutateDeal();
			toast.success("案件の取り下げを差し戻しました。");
			handleConfirmBackClose();
		} else {
			toast.error("エラーが発生しました。");
		}
		setIsSubmitting(false);
	};

	return (
		<div>
			{!isCanceled(deal.statusId) && (
				<div className={Styles.StatusCard}>
					<h2 className={Styles.Header}>
						{deal.companySetting.name}からの依頼
					</h2>
					{isSubVendor ? (
						<h4 className={Styles.SubVendorLabel}>
							施工会社：{getOriginVendor(deal)?.name}
						</h4>
					) : null}
					<StatusBar currentStatusId={null} statuses={displayStatuses} />
				</div>
			)}
			<div className={Styles.WorkflowCard}>
				<h3 className={Styles.CancelTitle}>
					{isCanceled(deal.statusId)
						? "この案件は取り下げられました"
						: "この案件の取り下げ申請が届きました"}
				</h3>
				<div>
					<div className={Styles.CancelReasonLabel}>取り下げ理由</div>
					{deal.dealCancel !== null && (
						<div className={Styles.CancelReason}>{deal.dealCancel.reason}</div>
					)}
					{!isCanceled(deal.statusId) &&
						!isCompleted(deal.statusId) &&
						!isSubVendor && (
							<div className={Styles.StatusHandleButtonRow}>
								<div className={Styles.RightButtons}>
									<button
										className={Styles.BackwardButton}
										onClick={handleConfirmBackOpen}
										type="button"
									>
										取り下げを差し戻して案件を継続する
									</button>
									<button
										className={Styles.ForwardButton}
										onClick={handleConfirmOpen}
										type="button"
									>
										取り下げを受理する
									</button>
								</div>
								<ConfirmModal
									actionText="継続"
									confirmType="save"
									disabled={isSubmitting}
									handleAccept={handleBackCancel}
									handleCancel={handleConfirmBackClose}
									isOpen={confirmBackOpen}
								>
									取り下げを差し戻して案件を継続します。
								</ConfirmModal>
								<ConfirmModal
									actionText="受理して終了"
									confirmType="delete"
									disabled={isSubmitting}
									handleAccept={handleAcceptCancel}
									handleCancel={handleConfirmClose}
									isOpen={confirmOpen}
								>
									取り下げを受理して案件を終了します。
									<Alertv2 mt={designTokens["spacing/m"]} variant="info">
										<Paragraph>
											受理すると、実施会社・管理会社それぞれに通知メールが送信されます。
										</Paragraph>
									</Alertv2>
								</ConfirmModal>
							</div>
						)}
				</div>
			</div>
		</div>
	);
}
